import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Sparkle, XCircle } from "@phosphor-icons/react";
import api from "../lib/api";
import { toast } from "sonner";

export default function AIQuizGenerator({ open, onClose, courses, assets, onCreated }) {
  const { t } = useTranslation();
  const [courseId, setCourseId] = useState("");
  const [assetId, setAssetId] = useState("");
  const [sourceText, setSourceText] = useState("");
  const [num, setNum] = useState(10);
  const [difficulty, setDifficulty] = useState("medium");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && courses?.length && !courseId) setCourseId(courses[0].course_id);
  }, [open, courses, courseId]);

  if (!open) return null;

  const courseAssets = (assets || []).filter(a => !courseId || a.course_id === courseId);

  const generate = async () => {
    if (!assetId && !sourceText.trim()) {
      toast.error("Provide a PDF/asset or paste source text");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        course_id: courseId,
        title: title || undefined,
        num_questions: Number(num) || 10,
        difficulty,
      };
      if (assetId) payload.asset_id = assetId;
      if (sourceText.trim()) payload.source_text = sourceText.trim();
      const { data } = await api.post("/lms/quizzes/generate", payload);
      toast.success(`Created ${data.questions_created} question${data.questions_created !== 1 ? "s" : ""} via Claude Sonnet 4.5`);
      onCreated?.(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI generation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="ai-quiz-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md w-full max-w-2xl shadow-xl border border-border max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-[hsl(244_53%_20%)] flex items-center justify-center">
              <Sparkle size={16} weight="fill" className="text-[hsl(20_90%_55%)]"/>
            </div>
            <div>
              <div className="font-heading font-bold text-base">Generate quiz with AI</div>
              <div className="text-[11px] text-muted-foreground">Powered by Claude Sonnet 4.5 · NEP/Bloom aligned</div>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="ai-modal-close-btn"><XCircle size={20} weight="fill"/></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eos-overline">Course</label>
              <select value={courseId} onChange={(e) => setCourseId(e.target.value)} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md bg-white" data-testid="ai-course-select">
                {(courses || []).map(c => <option key={c.course_id} value={c.course_id}>{c.course_id} — {c.title}</option>)}
              </select>
            </div>
            <div>
              <label className="eos-overline">Quiz title (optional)</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Trees & Graphs · Mock 1" className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid="ai-title-input"/>
            </div>
          </div>

          <div>
            <label className="eos-overline">Source · PDF from Content Library</label>
            <select value={assetId} onChange={(e) => setAssetId(e.target.value)} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md bg-white" data-testid="ai-asset-select">
              <option value="">— none (paste text below) —</option>
              {courseAssets.map(a => <option key={a.asset_id} value={a.asset_id}>{a.title || a.original_filename} ({(a.size / 1024).toFixed(0)} KB)</option>)}
            </select>
          </div>

          <div>
            <label className="eos-overline">…or paste source text</label>
            <textarea
              value={sourceText}
              onChange={(e) => setSourceText(e.target.value)}
              placeholder="Paste lesson notes, syllabus, or textbook excerpt here…"
              rows={6}
              className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md font-sans"
              data-testid="ai-source-text"
            />
            <div className="text-[10px] text-muted-foreground mt-1">{sourceText.length.toLocaleString()} chars · capped at 12,000 server-side</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eos-overline">Number of questions</label>
              <input type="number" min={1} max={20} value={num} onChange={(e) => setNum(e.target.value)} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid="ai-num-input"/>
            </div>
            <div>
              <label className="eos-overline">Difficulty</label>
              <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md bg-white" data-testid="ai-difficulty-select">
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-border flex items-center justify-between bg-secondary/30">
          <div className="text-[11px] text-muted-foreground">
            {assetId ? "Using PDF/asset content" : sourceText.trim() ? `Using pasted text (${sourceText.trim().length} chars)` : "Add an asset or text to enable"}
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={onClose} variant="outline" className="rounded-md" data-testid="ai-cancel-btn">{t("common.cancel", "Cancel")}</Button>
            <Button onClick={generate} disabled={busy || (!assetId && !sourceText.trim())} className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="ai-generate-btn">
              <Sparkle size={14} weight="fill" className="mr-1.5 text-[hsl(20_90%_55%)]"/>
              {busy ? "Generating…" : "Generate"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
